/**
 * MONTAGE · Benachrichtigung, sobald eine Variante wieder da ist
 * ═══════════════════════════════════════════════════════
 *
 * Trägt eine E-Mail-Adresse bei Klaviyo auf eine ausverkaufte Variante ein.
 * Klaviyo erkennt den Bestand über seine eigene Shopify-Anbindung und
 * verschickt die Nachricht selbst. Das Theme meldet nur an.
 *
 * Endpunkt: POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
 *           ?company_id=<öffentlicher Schlüssel>
 * Variantenkennung für Shopify: $shopify:::$default:::<variant_id>
 *
 * ⚠️ Der Variantenwechsel ist der Punkt, an dem so etwas üblicherweise
 *    bricht. Dawn tauscht beim Wechsel NICHT den ganzen Kaufblock aus,
 *    sondern nur Preis, Bestand und ein paar Felder, und schaltet den
 *    Kaufknopf über `toggleSubmitButton` scharf oder stumpf. Ein Block, der
 *    daneben steht, bliebe stehen wie er war: der Knopf böte dann eine
 *    Benachrichtigung für eine Variante an, die längst lieferbar ist.
 *    Deshalb hängt dieses Skript an Dawns eigenem Ereignis `variantChange`
 *    aus `assets/pubsub.js`. Das ist Dawns vorgesehener Weg und überlebt
 *    ein Dawn-Update, solange das Ereignis existiert. Fehlt pubsub.js,
 *    passiert nichts Schlimmes: der serverseitig gerenderte Zustand bleibt
 *    stehen und stimmt für die zuerst gewählte Variante.
 *
 * ⛔ Kein Text steht hier fest. Alles kommt über Datenattribute aus
 *    `snippets/mt-texte.liquid`, siehe EINGRIFFSREGELN.md § 6.2. Die
 *    englischen Rückfalltexte sind eine Notbremse, kein zweiter Textkanal.
 */
(function () {
  'use strict';

  var ENDPUNKT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-10-15';

  /**
   * Der öffentliche Klaviyo-Schlüssel.
   *
   * Erste Quelle ist die Theme-Einstellung. Fehlt sie, wird der Schlüssel aus
   * dem Skript-Tag gelesen, den die Klaviyo-App selbst einbindet. Damit
   * funktioniert der Eintrag auch dann, wenn niemand daran gedacht hat, ihn
   * in den Theme-Einstellungen zu hinterlegen. Beide bekannten Formen der
   * Adresse werden abgedeckt:
   *   .../onsite/js/<KEY>/klaviyo.js
   *   .../onsite/js/klaviyo.js?company_id=<KEY>
   */
  function schluesselFinden(wurzel) {
    var ausEinstellung = wurzel.dataset.schluessel;
    if (ausEinstellung) return ausEinstellung;

    var tag = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
    if (!tag) return '';

    var quelle = tag.getAttribute('src') || '';
    var ausAbfrage = quelle.match(/[?&]company_id=([A-Za-z0-9]+)/);
    if (ausAbfrage) return ausAbfrage[1];

    var ausPfad = quelle.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/);
    return ausPfad ? ausPfad[1] : '';
  }

  function text(wurzel, name, rueckfall) {
    return wurzel.dataset[name] || rueckfall;
  }

  function gueltigeAdresse(wert) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wert);
  }

  customElements.define(
    'mt-bis',
    class MtBis extends HTMLElement {
      connectedCallback() {
        this.fenster = document.getElementById(this.dataset.fenster);
        this.formular = this.querySelector('[data-mt-bis-formular]');
        if (!this.fenster || !this.formular) return;

        this.querySelectorAll('[data-mt-bis-oeffnen]').forEach((knopf) => {
          knopf.addEventListener('click', this.oeffnen.bind(this));
        });
        this.querySelectorAll('[data-mt-bis-schliessen]').forEach((knopf) => {
          knopf.addEventListener('click', this.schliessen.bind(this));
        });

        // Auslöser ausserhalb dieses Elements, heute die Sticky-Leiste. Sie
        // sollen dasselbe Fenster öffnen und dieselbe Sichtbarkeit tragen,
        // damit die beiden Kaufwege nicht auseinanderlaufen.
        this.fremdoeffner = document.querySelectorAll('[data-mt-bis-fremdoeffner]');
        this.fremdoeffner.forEach((knopf) => {
          knopf.addEventListener('click', this.oeffnen.bind(this));
        });

        // Klick auf die Fläche neben dem Blatt schließt. `<dialog>` selbst
        // füllt den ganzen Bildschirm, das Blatt darin ist der sichtbare
        // Kasten. Trifft der Klick den Dialog und nicht das Blatt, lag er
        // daneben.
        this.fenster.addEventListener('click', (ereignis) => {
          if (ereignis.target === this.fenster) this.schliessen();
        });

        this.formular.addEventListener('submit', this.absenden.bind(this));

        this.anVariantenwechselHaengen();
      }

      /** Dawns Ereignis beim Variantenwechsel, siehe Kopfkommentar. */
      anVariantenwechselHaengen() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        // Nur auf den eigenen Kaufblock hören. Auf einer Seite mit mehreren
        // Produktsektionen würde sonst jede Sektion auf jede andere reagieren.
        var meineSektion = this.closest('.shopify-section');

        this.abmelden = subscribe(PUB_SUB_EVENTS.variantChange, (nachricht) => {
          var daten = nachricht && nachricht.data;
          if (!daten) return;
          if (meineSektion && meineSektion.id.indexOf(daten.sectionId) === -1) return;

          var variante = daten.variant;

          // Keine Variante heißt: diese Kombination gibt es nicht. Dann gibt
          // es auch nichts einzutragen.
          if (!variante) {
            this.sichtbarkeit(false);
            return;
          }

          this.dataset.variante = variante.id;
          this.sichtbarkeit(variante.available !== true);
          this.produktzeileSetzen(variante);
          this.zustand('formular');
        });
      }

      /**
       * Ein Schalter für alle Auslöser. Der eigene Block und jeder
       * Fremdauslöser tragen denselben Zustand, sonst böte die Leiste eine
       * Benachrichtigung für eine längst lieferbare Variante an.
       */
      sichtbarkeit(zeigen) {
        this.hidden = !zeigen;
        if (this.fremdoeffner) {
          this.fremdoeffner.forEach((knopf) => {
            knopf.hidden = !zeigen;
          });
        }
      }

      produktzeileSetzen(variante) {
        var zeile = this.querySelector('[data-mt-bis-produkt]');
        if (!zeile) return;

        var titel = zeile.dataset.produkttitel || '';
        if (!titel) {
          // Beim ersten Mal steht der Produktname serverseitig in der Zeile.
          // Er wird hier gemerkt, damit der Variantenname später sauber
          // angehängt werden kann, ohne ihn doppelt zu schreiben.
          titel = (zeile.textContent || '').split(' · ')[0].trim();
          zeile.dataset.produkttitel = titel;
        }

        var name = variante.title && variante.title !== 'Default Title' ? variante.title : '';
        zeile.textContent = name ? titel + ' · ' + name : titel;
      }

      zustand(welcher) {
        this.fenster.querySelectorAll('[data-mt-bis-zustand]').forEach((teil) => {
          teil.hidden = teil.dataset.mtBisZustand !== welcher;
        });
      }

      oeffnen() {
        this.fehlerZeigen('');
        this.zustand('formular');

        var feld = this.formular.querySelector('input[type="email"]');
        if (feld) feld.value = '';

        // showModal hebt das Fenster in die oberste Ebene des Browsers und
        // bringt Escape-Taste und Fokusfalle mit. Fehlt die Methode, bleibt
        // das Fenster wenigstens sichtbar.
        if (typeof this.fenster.showModal === 'function') {
          this.fenster.showModal();
        } else {
          this.fenster.setAttribute('open', '');
        }

        if (feld) window.setTimeout(function () { feld.focus(); }, 60);
      }

      schliessen() {
        if (typeof this.fenster.close === 'function' && this.fenster.open) {
          this.fenster.close();
        } else {
          this.fenster.removeAttribute('open');
        }
      }

      laden(an) {
        var kreis = this.querySelector('[data-mt-bis-laden]');
        var knopf = this.formular.querySelector('button[type="submit"]');
        if (kreis) kreis.classList.toggle('hidden', !an);
        if (knopf) knopf.disabled = an;
      }

      fehlerZeigen(nachricht) {
        var feld = this.querySelector('[data-mt-bis-fehler]');
        if (!feld) return;
        feld.textContent = nachricht;
        feld.hidden = !nachricht;
      }

      absenden(ereignis) {
        ereignis.preventDefault();

        var feld = this.formular.querySelector('input[type="email"]');
        var adresse = feld ? feld.value.trim() : '';

        if (!gueltigeAdresse(adresse)) {
          this.fehlerZeigen(text(this, 'textFehlerMail', 'Please enter a valid email address.'));
          if (feld) feld.focus();
          return;
        }

        var schluessel = schluesselFinden(this);
        var variante = this.dataset.variante;

        if (!schluessel || !variante) {
          this.fehlerZeigen(text(this, 'textFehlerAllgemein', 'Sign-up failed. Please try again later.'));
          return;
        }

        this.fehlerZeigen('');
        this.laden(true);

        // Die Adresse zusätzlich an Klaviyos Onsite-Skript geben, damit das
        // Profil auf dieser Sitzung wiedererkannt wird. Läuft die App nicht,
        // ist das folgenlos.
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          try {
            window.klaviyo.push(['identify', { $email: adresse }]);
          } catch (fehler) {
            /* ohne Belang */
          }
        }

        var koerper = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: adresse },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: '$shopify:::$default:::' + variante,
                },
              },
            },
          },
        };

        fetch(ENDPUNKT + '?company_id=' + encodeURIComponent(schluessel), {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: REVISION,
          },
          body: JSON.stringify(koerper),
        })
          .then((antwort) => {
            // Klaviyo antwortet auf den Eintrag mit 202 und ohne Inhalt.
            if (!antwort.ok && antwort.status !== 202) throw new Error('klaviyo-' + antwort.status);
            this.laden(false);
            this.zustand('erfolg');
          })
          .catch(() => {
            this.laden(false);
            this.fehlerZeigen(text(this, 'textFehlerAllgemein', 'Sign-up failed. Please try again later.'));
          });
      }

      disconnectedCallback() {
        if (typeof this.abmelden === 'function') this.abmelden();
      }
    }
  );
})();
